import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { BugService } from 'src/app/private/services/bug.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-project-detail-bugs',
  templateUrl: './project-detail-bugs.component.html',
  styleUrls: ['./project-detail-bugs.component.scss']
})
export class ProjectDetailBugsComponent implements OnInit, OnChanges {
  @Input() projectId: any;
  @Input() selectedItem: any = null;
  @Input() projectMembers: any[] = [];
  @Input() currentUserId: any;
  @Input() sprints: any[] = [];
  @Input() modules: any[] = [];

  @Output() bugSelected = new EventEmitter<any>();
  @Output() bugEdit = new EventEmitter<any>();
  @Output() bugDelete = new EventEmitter<{ bug: any; event: Event }>();
  @Output() addBugClicked = new EventEmitter<void>();

  allProjectBugs: any[] = [];
  loadingAllBugs = false;
  totalRows = 0;

  bugSearchQuery = '';
  priorityFilter = 'all';
  severityFilter = 'all';
  statusFilter = 'all';
  assigneeFilter = 'all';
  testerFilter = 'all';
  sprintFilter = 'all';
  moduleFilter = 'all';
  onlyMyBugs = false;
  onlyMyTested = false;
  onlyOverdue = false;

  pageIndex = 0;
  pageSize = 20;
  pageSizeOptions = [10, 25, 50, 100];

  loadingMore = false;
  hasMore = true;

  readonly bugStatusFilterOptions = [
    { value: 'open', label: 'Open' },
    { value: 're-open', label: 'Re-open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'in_review', label: 'In Review' },
    { value: 'ready_for_test', label: 'Ready for Test' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'closed', label: 'Closed' },
  ];

  statusGroupsList = [
    'open',
    're-open',
    'in_progress',
    'in_review',
    'ready_for_test',
    'rejected',
    'closed',
    'other',
  ];

  expandedStatusGroups: { [key: string]: boolean } = {
    open: true,
    're-open': true,
    in_progress: true,
    in_review: true,
    ready_for_test: true,
    rejected: true,
    closed: true,
    other: true,
  };

  constructor(
    private bugService: BugService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadBugs();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.pageIndex = 0;
      this.loadBugs();
    }
  }

  loadBugs(append: boolean = false) {
    if (!append) {
      this.pageIndex = 0;
      this.allProjectBugs = [];
      this.loadingAllBugs = true;
    } else {
      this.loadingMore = true;
    }

    const whereClause: any = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId()
    };

    if (this.priorityFilter && this.priorityFilter !== 'all') {
      whereClause.priority = this.priorityFilter;
    }

    if (this.severityFilter && this.severityFilter !== 'all') {
      whereClause.severity = this.severityFilter;
    }

    if (this.statusFilter && this.statusFilter !== 'all') {
      whereClause.status = this.statusFilter;
    }

    if (this.assigneeFilter && this.assigneeFilter !== 'all') {
      whereClause.assigned_to = this.assigneeFilter;
    }

    if (this.testerFilter && this.testerFilter !== 'all') {
      whereClause.tester_id = this.testerFilter;
    }

    if (this.sprintFilter && this.sprintFilter !== 'all') {
      if (this.sprintFilter === 'backlog') {
        whereClause.sprint_id = null;
      } else {
        whereClause.sprint_id = Number(this.sprintFilter);
      }
    }

    if (this.moduleFilter && this.moduleFilter !== 'all') {
      whereClause.module_id = Number(this.moduleFilter);
    }

    if (this.onlyMyBugs && this.currentUserId) {
      whereClause.assigned_to = this.currentUserId;
    }

    if (this.onlyMyTested && this.currentUserId) {
      whereClause.tester_id = this.currentUserId;
    }

    const payload: any = {
      page: this.pageIndex * this.pageSize, // offset-based pagination on bugs controller
      limit: this.pageSize,
      user_id: this.localStorage.getUserId(),
      where: whereClause,
      order: {
        col: '"bug"."updated_at"',
        dir: 'DESC'
      }
    };

    if (this.bugSearchQuery?.trim()) {
      payload.filter = {
        title: this.bugSearchQuery.trim()
      };
    }

    this.bugService.getAllBugs(payload).subscribe({
      next: (res: any) => {
        const rows = res?.rows || res?.data?.rows || [];
        this.totalRows = res?.count || res?.data?.count || 0;

        let processedRows = rows;
        if (this.onlyOverdue) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          processedRows = rows.filter((b: any) => b.due_date && new Date(b.due_date) < today && b.status !== 'closed');
        }

        if (append) {
          this.allProjectBugs = [...this.allProjectBugs, ...processedRows];
        } else {
          this.allProjectBugs = processedRows;
        }

        this.hasMore = this.allProjectBugs.length < this.totalRows;
        this.loadingAllBugs = false;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingAllBugs = false;
        this.loadingMore = false;
        this.toastr.error('Failed to load bugs');
      }
    });
  }

  onFilterChange() {
    this.pageIndex = 0;
    this.loadBugs(false);
  }

  onTableScroll(event: any) {
    const el = event.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      if (!this.loadingAllBugs && !this.loadingMore && this.hasMore) {
        this.pageIndex++;
        this.loadBugs(true);
      }
    }
  }

  normalizeStatusKey(s: any): string {
    if (!s) return '';
    return String(s).toLowerCase().replace(/[\s_-]+/g, '');
  }

  getBugsByStatus(statusGroupKey: string) {
    const targetKey = this.normalizeStatusKey(statusGroupKey);
    const bugs = this.allProjectBugs || [];

    if (targetKey === 'other') {
      const knownGroupKeys = this.statusGroupsList
        .filter((key) => key !== 'other')
        .map((key) => this.normalizeStatusKey(key));
      return bugs.filter((b) => {
        const k = this.normalizeStatusKey(b.status);
        return !knownGroupKeys.includes(k) && k !== 'assigned' && k !== 'resolved';
      });
    }

    return bugs.filter((b) => {
      const bugStatusKey = this.normalizeStatusKey(b.status);
      if (targetKey === 'open') {
        return bugStatusKey === 'open' || bugStatusKey === 'assigned';
      }
      return bugStatusKey === targetKey;
    });
  }

  getStatusPillClass(status: string): string {
    const k = this.normalizeStatusKey(status);
    if (k === 'open' || k === 'assigned') return 'status-pill--open';
    if (k === 'inprogress') return 'status-pill--in_progress';
    if (k === 'inreview') return 'status-pill--in_review';
    if (k === 'readyfortest') return 'status-pill--ready_for_test';
    if (k === 'reopen') return 'status-pill--re-open';
    if (k === 'rejected') return 'status-pill--rejected';
    if (k === 'closed') return 'status-pill--closed';
    if (k === 'resolved') return 'status-pill--resolved';
    return 'status-pill--open';
  }

  getStatusLabel(status: string): string {
    const match = this.bugStatusFilterOptions.find(
      (option) => this.normalizeStatusKey(option.value) === this.normalizeStatusKey(status),
    );
    if (match) {
      return match.label;
    }

    switch (status) {
      case 'open':
        return 'Open';
      case 're-open':
        return 'Re-open';
      case 'in_progress':
        return 'In Progress';
      case 'in_review':
        return 'In Review';
      case 'ready_for_test':
        return 'Ready for Test';
      case 'rejected':
        return 'Rejected';
      case 'closed':
        return 'Closed';
      default:
        return this.prettyLabel(status);
    }
  }

  prettyLabel(val: string): string {
    if (!val) return '';
    return val.replace(/_/g, ' ');
  }

  getPriorityClass(priority: string): string {
    if (!priority) return 'priority-low';
    return `priority-${priority.toLowerCase()}`;
  }

  getSeverityClass(severity: string): string {
    if (!severity) return 'severity-medium';
    return `severity-${severity.toLowerCase()}`;
  }

  getAvatarColor(user: any): string {
    if (!user || !user.fname) return '#ef4444';
    const colors = ['#ec4899', '#f43f5e', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];
    const idx = (user.fname.charCodeAt(0) + (user.lname?.charCodeAt(0) || 0)) % colors.length;
    return colors[idx];
  }

  getUserObject(userRef: any): any {
    if (!userRef) return null;
    if (typeof userRef === 'object' && (userRef.fname || userRef.lname || userRef.first_name)) {
      return userRef;
    }
    const uid = Number(userRef);
    if (!uid) return null;
    return (this.projectMembers || []).find((m) => Number(m.user_id) === uid) || null;
  }

  getReporterUser(bug: any): any {
    if (!bug) return null;
    return (
      this.getUserObject(bug.reporter) ||
      this.getUserObject(bug.reporter_id) ||
      this.getUserObject(bug.tester) ||
      this.getUserObject(bug.tester_id) ||
      this.getUserObject(bug.created_by_user) ||
      this.getUserObject(bug.created_by) ||
      null
    );
  }

  getInitials(user: any): string {
    if (!user) return '?';
    const f = user.fname ? user.fname.charAt(0).toUpperCase() : '';
    const l = user.lname ? user.lname.charAt(0).toUpperCase() : '';
    return f + l;
  }

  toggleStatusGroup(status: string) {
    this.expandedStatusGroups[status] = !this.expandedStatusGroups[status];
  }

  updateBugStatus(bug: any, newStatus: string) {
    const prev = bug.status;
    bug.status = newStatus;
    this.bugService
      .updateBug({
        bug_id: bug.bug_id,
        where: { bug_id: bug.bug_id },
        status: newStatus,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Bug status updated successfully');
          this.loadBugs();
        },
        error: () => {
          bug.status = prev;
          this.toastr.error('Failed to update bug status');
        },
      });
  }
}
